/* eslint-disable object-shorthand */
import { ULS, TraceLevel, LogCategory } from "@1js/office-online-telemetry";
/** Extracts the `offset` and `duration` values and returns them converted into milliseconds. */
export var getOffsetAndDurationInMs = function (jsonResult) {
    // Divide each by 10,000 to convert them into milliseconds
    var offsetMs = jsonResult.Offset / 1e4;
    var durationMs = jsonResult.Duration / 1e4;
    return { offsetMs: offsetMs, durationMs: durationMs };
};
/** Track the count and totals of MeasuredLatency's to calculate average values at the end of the session */
var RunningAveragesTracker = /** @class */ (function () {
    function RunningAveragesTracker() {
        this.count = 0;
        this.total = { service: 0, perceived: 0 };
        this.min = {
            service: Number.MAX_VALUE,
            perceived: Number.MAX_VALUE,
        };
        this.max = { service: 0, perceived: 0 };
    }
    RunningAveragesTracker.prototype.add = function (latency) {
        this.count++;
        this.total.perceived += latency.perceived;
        this.total.service += latency.service;
        this.min.perceived = Math.min(latency.perceived, this.min.perceived);
        this.min.service = Math.min(latency.service, this.min.service);
        this.max.perceived = Math.max(latency.perceived, this.max.perceived);
        this.max.service = Math.max(latency.service, this.max.service);
    };
    RunningAveragesTracker.prototype.getAverages = function () {
        if (this.count === 0) {
            return { perceived: -1, service: -1 };
        }
        return {
            perceived: this.total.perceived / this.count,
            service: this.total.service / this.count,
        };
    };
    return RunningAveragesTracker;
}());
export { RunningAveragesTracker };
/** Tracks the local absolute times for audio before sending it to a server, so we can measure durations between audio going up and the responses back. This is effectively a copy of the same class in
 *  [AudioStreamPerfTracker.ts](https://dev.azure.com/office/ISS/_git/augloop-workflows?path=%2Futils%2Faudio-utils%2Fsrc%2FAudioStreamPerfTracker.ts&version=GBmain&_a=contents) in repo *augloop-workflows*.
 */
var AudioStreamPerfTracker = /** @class */ (function () {
    function AudioStreamPerfTracker(max, getNow) {
        this.finals = new RunningAveragesTracker();
        this.partials = new RunningAveragesTracker();
        this.getNow = Date.now; // for overriding for mocking in tests
        this.list = [];
        this.maxListLength = max;
        this.warnListLength = max * 0.8; // set the warning level at 80% of the max
        this.getNow = getNow !== null && getNow !== void 0 ? getNow : Date.now;
    }
    Object.defineProperty(AudioStreamPerfTracker.prototype, "length", {
        /** Returns the count of tracked audio chunks */
        get: function () {
            return this.list.length;
        },
        enumerable: false,
        configurable: true
    });
    /** Returns an audio chunk by index */
    AudioStreamPerfTracker.prototype.getChunk = function (idx) {
        return this.list[idx];
    };
    /** Tracks that we processed audio for the audio timings given against the local-time of *now* (when called). */
    AudioStreamPerfTracker.prototype.track = function (audioEnd, duration, seq) {
        var now = this.getNow();
        var newChunk = {
            audioStart: audioEnd - duration,
            audioEnd: audioEnd,
            localEnd: now,
            seq: seq,
        };
        // we need the array to be ordered and it's likely we'll track younger tiles that go on the
        // end however it's not uncommon for tiles to arrive out of order so we'll need to walk the
        // array backwards to insert chunks as necessary
        var idx = this.list.length - 1;
        if (idx === -1 || newChunk.audioStart >= this.list[idx].audioEnd) {
            // add to end
            this.list.push(newChunk);
        }
        else if (newChunk.audioEnd <= this.list[0].audioStart) {
            // insert at beginning
            this.list.unshift(newChunk);
        }
        else {
            // find location somewhere in the middle
            while (idx > 0 && newChunk.audioEnd < this.list[idx].audioStart) {
                idx--;
            }
            // confirm we never track the same audio chunk twice
            if (idx >= 0 && newChunk.audioStart === this.list[idx].audioStart) {
                throw new Error("Why are we double tracking the same chunk of audio? newChunk: ".concat(JSON.stringify(newChunk), ", existing chunk: ").concat(JSON.stringify(this.list[idx])));
            }
            this.list.splice(idx, 0, newChunk);
        }
        // if the list is getting too long, throw out the oldest chunk
        if (this.list.length > this.maxListLength) {
            var excess = this.list.length - this.maxListLength;
            this.list.splice(0, excess);
            ULS.sendTraceTag(0x1f350419 /* tag_5nqqz */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "AudioStreamPerfTracker.track: list length has exceeded max by ".concat(excess, " items!"));
        }
        else if (this.list.length >= this.warnListLength) {
            ULS.sendTraceTag(0x1f35041a /* tag_5nqq0 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "AudioStreamPerfTracker.track: list length: ".concat(this.list.length, " approaching max: ").concat(this.maxListLength));
        }
    };
    /** Discards timings for audio chunks that occurred before the given end time. */
    AudioStreamPerfTracker.prototype.trimBefore = function (audioEnd) {
        if (this.list.length === 0) {
            ULS.sendTraceTag(0x1f35041b /* tag_5nqq1 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "AudioStreamPerfTracker.trimBefore: list is empty - nothing to trim!");
            return;
        }
        var audioSpanBeforeTrim = "".concat(this.list[0].audioStart, " to ").concat(this.list[this.list.length - 1].audioEnd);
        var idx = 0;
        while (idx < this.list.length && this.list[idx].audioEnd < audioEnd) {
            idx++;
        }
        if (idx === this.list.length) {
            ULS.sendTraceTag(0x1f35041c /* tag_5nqq2 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "AudioStreamPerfTracker.trimBefore: no match found for audioEnd: ".concat(audioEnd, ". List length: ").concat(this.list.length, ", audio span: ").concat(audioSpanBeforeTrim));
        }
        var removedChunks = this.list.splice(0, idx);
        var audioSpanRemoved = removedChunks.length > 0
            ? "".concat(removedChunks[0].audioStart, " to ").concat(removedChunks[removedChunks.length - 1].audioEnd)
            : "none";
        var audioSpanAfterTrim = this.list.length > 0
            ? "".concat(this.list[0].audioStart, " to ").concat(this.list[this.list.length - 1].audioEnd)
            : "none";
        ULS.sendTraceTag(0x1f35041d /* tag_5nqq3 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "AudioStreamPerfTracker.trimBefore: audioEnd: ".concat(audioEnd, ", culling ").concat(removedChunks.length, " chunks (audio span ").concat(audioSpanRemoved, "), new list.length: ").concat(this.list.length, ". Audio span was: ").concat(audioSpanBeforeTrim, ", now: ").concat(audioSpanAfterTrim));
    };
    /** Returns the audio chunks that contain the start (offset) and end (offset+duration) points in audio time. */
    AudioStreamPerfTracker.prototype.getChunksFrom = function (speechOffset, speechDuration) {
        var speechEnd = speechOffset + speechDuration;
        // find the audio chunks that contain the start and end of the utterance
        var idx = 0;
        var start;
        var end;
        while (idx < this.list.length && (!start || !end)) {
            var chunk = this.list[idx];
            if (!start &&
                speechOffset >= chunk.audioStart &&
                speechOffset <= chunk.audioEnd) {
                start = chunk;
            }
            if (!end &&
                speechEnd >= chunk.audioStart &&
                speechEnd <= chunk.audioEnd) {
                end = chunk;
            }
            idx++;
        }
        return [start, end];
    };
    /** Calculates latencies from the given ASR offset/duration values.
     * These are calculated from both the start and end points with the larger value of each returned. */
    AudioStreamPerfTracker.prototype.measureLatency = function (speechOffset, speechDuration, isFinal) {
        var now = this.getNow();
        var speechEnd = speechOffset + speechDuration;
        // find the audio chunks that contain the start and end of the utterance
        var _a = this.getChunksFrom(speechOffset, speechDuration), chunkStart = _a[0], chunkEnd = _a[1];
        if (!chunkStart && !chunkEnd) {
            // zero audio chunk were found - we cannot measure any latencies
            ULS.sendTraceTag(0x1f35041e /* tag_5nqq4 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "AudioStreamPerfTracker.getRelatedAudioChunks: Zero audio chunks not found for speechEnd: ".concat(speechEnd, " (").concat(speechOffset, " + ").concat(speechDuration, ")"));
            return { service: -1, perceived: -1 };
        }
        else if (!chunkStart || !chunkEnd) {
            // one of the expected audio chunks was not found - this is a bit weird but not catastrophic
            var startOrEnd = !chunkStart ? "start" : "end";
            var currentAudioSpan = "none";
            var delta = NaN;
            if (this.list.length > 0) {
                currentAudioSpan = "".concat(this.list[0].audioStart, " to ").concat(this.list[this.list.length - 1].audioEnd);
                delta = !chunkStart
                    ? speechEnd - this.list[0].audioStart // how far before tracked audio
                    : speechEnd - this.list[this.list.length - 1].audioEnd; // how far after tracked audio
            }
            ULS.sendTraceTag(0x1f35041f /* tag_5nqq5 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "AudioStreamPerfTracker.getRelatedAudioChunks: Expected audio chunk not found for ".concat(startOrEnd, " of audio, for speechEnd: ").concat(speechEnd, " (").concat(speechOffset, " + ").concat(speechDuration, "), tracked audio span: ").concat(currentAudioSpan, ", delta: ").concat(delta));
        }
        // the service latency measures raw latency between us and the service we rely on - regardless of where the
        // utterance starts/stops within the audio chunks
        var serviceLatency = now - Math.max((chunkStart === null || chunkStart === void 0 ? void 0 : chunkStart.localEnd) || 0, (chunkEnd === null || chunkEnd === void 0 ? void 0 : chunkEnd.localEnd) || 0);
        // calculate the latencies from the start AND end of the audio
        var startLatency = chunkStart
            ? this.getLatencyForChunk(chunkStart, speechOffset, speechDuration, now)
            : -1;
        var endLatency = chunkEnd
            ? this.getLatencyForChunk(chunkEnd, speechEnd, 0, now)
            : -1;
        var perceivedLatency = Math.max(startLatency, endLatency);
        var latency = {
            service: serviceLatency,
            perceived: perceivedLatency,
        };
        // keep track of partial/final running totals
        (isFinal ? this.finals : this.partials).add(latency);
        return latency;
    };
    /** Calculates the latency for the given chunk against a given ASR offset and duration. */
    AudioStreamPerfTracker.prototype.getLatencyForChunk = function (chunk, speechOffset, speechDuration, now) {
        var localOffset = chunk.localEnd - (chunk.audioEnd - speechOffset);
        var latency = now - localOffset - speechDuration;
        return latency;
    };
    return AudioStreamPerfTracker;
}());
export { AudioStreamPerfTracker };
//# sourceMappingURL=AudioStreamPerfTracker.js.map